import type { ActivityDetail, StreamsPayload, Zone } from '@painpal/contracts';
import { ZONE_LABELS, type ZoneModel } from '@painpal/core';
import { AXIS_TEXT, chartTooltip, pointValue } from '../../lib/chartTheme';
import type { ECOption } from '../../lib/echarts';
import { formatWhole, orDash } from '../../lib/format';
import { itemAt } from '../../lib/zones';
import { colors, ZONE_PALETTES } from '../../theme/tokens';
import { timeAxis } from './streamTracks';

export const HR_CHART_LABEL = 'Heart rate over time with HR zones';
export const HR_CHART_HEIGHT = { desktop: 260, phone: 180 } as const;
const HR_CEILING = 240;

// The board's bands lay the shared zone scale under the line: zone 1 at 20% opacity, the rest at 25%.
export const bandColor = (hex: string, index: number): string => `${hex}${index === 0 ? '33' : '40'}`;

type Band = [{ yAxis: number; itemStyle: { color: string } }, { yAxis: number }];

export function zoneBands(zone: Zone, model: ZoneModel): Band[] {
  let low = 0;
  return [...zone.bounds, HR_CEILING].map((high, i): Band => {
    const band: Band = [{ yAxis: low, itemStyle: { color: bandColor(itemAt(ZONE_PALETTES[model], i), i) } }, { yAxis: high }];
    low = high;
    return band;
  });
}

export function hrChartSub(zone: Zone, model: ZoneModel): string {
  return `BANDS = HR ZONES Z1–${itemAt(ZONE_LABELS[model], ZONE_LABELS[model].length - 1)} · LINE = THRESHOLD HR ${zone.threshold}`;
}

export function hrChartStat(a: Pick<ActivityDetail, 'avgHr' | 'maxHr'>): string {
  return `AVG ${orDash(a.avgHr, formatWhole)} · MAX ${orDash(a.maxHr, formatWhole)} BPM`;
}

const hrTooltip = (params: unknown): string => {
  const value = pointValue(params);
  return value === null ? '—' : `${value} BPM`;
};

export function hrChartOption(streams: StreamsPayload, zone: Zone, model: ZoneModel, phone: boolean): ECOption {
  const maxT = streams.t.at(-1) ?? 0;
  const time = timeAxis(maxT);
  return {
    grid: { left: 36, right: 8, top: 8, bottom: 24 },
    xAxis: {
      type: 'value', min: 0, max: maxT, interval: time.interval, splitLine: { show: false }, axisTick: { show: false },
      axisLine: { lineStyle: { color: colors.ink } }, axisLabel: { ...AXIS_TEXT, ...time.axisLabel },
    },
    yAxis: { type: 'value', min: (extent: { min: number }) => Math.floor((extent.min - 10) / 20) * 20, splitLine: { show: false }, axisLabel: AXIS_TEXT },
    tooltip: chartTooltip({ phone, formatter: hrTooltip }),
    series: [{
      type: 'line', name: 'HEART RATE', data: streams.t.map((t, i): [number, number | null] => [t, streams.hr?.[i] ?? null]),
      showSymbol: false, sampling: 'lttb', connectNulls: false, lineStyle: { color: colors.hot, width: 1.6 }, itemStyle: { color: colors.hot },
      markArea: { silent: true, data: zoneBands(zone, model) },
      markLine: { silent: true, symbol: 'none', label: { show: false }, lineStyle: { color: colors.hot, width: 1, type: 'solid' }, data: [{ yAxis: zone.threshold }] },
    }],
  };
}
